import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, RefreshCw } from 'lucide-react';
import { getUsageSnapshot } from '@/api/usage';
import type { AiQuotaKey, BillingResourceKey } from '@/api/billing';
import { formatStorageBytes } from '@trove/note-sanitize';

function Bar({
  label,
  used,
  max,
  formatValue,
}: {
  label: string;
  used: number;
  max: number;
  formatValue?: (n: number) => string;
}) {
  const fmt = formatValue ?? ((n: number) => String(n));
  const pct = max <= 0 ? 0 : Math.min(100, Math.round((used / max) * 100));
  const warn = pct >= 80 && pct < 100;
  const bad = pct >= 100;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
        <span>{label}</span>
        <span className={bad ? 'text-red-500' : warn ? 'text-amber-500' : 'text-foreground'}>
          {fmt(used)} / {fmt(max)}
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-muted">
        <div
          className={`h-full rounded-full transition-all ${bad ? 'bg-red-500' : warn ? 'bg-amber-500' : 'bg-primary'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

const RESOURCE_LABEL_KEYS: Partial<Record<BillingResourceKey, string>> = {
  bookmark: 'quota.resource.bookmark',
  notes: 'quota.resource.notes',
  highlighter: 'quota.resource.highlighter',
  workspace: 'quota.resource.workspace',
  users: 'quota.resource.users',
  smartCollections: 'quota.resource.smartCollections',
  noteMediaStorage: 'quota.resource.noteMediaStorage',
};

const AI_LABEL_KEYS: Record<AiQuotaKey, string> = {
  ai_search: 'quota.aiBuckets.search',
  ai_assistant: 'quota.aiBuckets.assistant',
  ai_suggest: 'quota.aiBuckets.suggest',
  ai_summarize: 'quota.aiBuckets.summarize',
  ai_resurface: 'quota.aiBuckets.resurface',
};

/** Compact quota overview — driven by GET `/usage?view=quota`. */
export function QuotaMeter({ refreshKey = 0 }: { refreshKey?: number }) {
  const { t } = useTranslation('settings');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [snap, setSnap] = useState<Awaited<ReturnType<typeof getUsageSnapshot>> | null>(null);

  const load = useCallback(async (force: boolean) => {
    const u = await getUsageSnapshot(force, { view: 'quota' });
    setSnap(u);
    setErr(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void load(refreshKey > 0)
      .catch(() => {
        if (!cancelled) setErr(t('quota.loadFailed'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [load, t, refreshKey]);

  const onRefresh = () => {
    setRefreshing(true);
    void load(true)
      .catch(() => setErr(t('quota.loadFailed')))
      .finally(() => setRefreshing(false));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (err || !snap) {
    return <p className="text-sm text-muted-foreground">{err ?? t('quota.loadFailed')}</p>;
  }

  const resourceKeys = (Object.keys(snap.resources).filter((k) => {
    if (k === 'noteMediaStorage' && (snap.resources.noteMediaStorage?.max ?? 0) <= 0) return false;
    return Boolean(RESOURCE_LABEL_KEYS[k as BillingResourceKey]);
  }) as BillingResourceKey[]);
  const ai = snap.ai ?? {};

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">{t('quota.staleHint')}</p>
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-primary hover:bg-primary/10 disabled:opacity-50"
        >
          <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} aria-hidden />
          {t('quota.refresh')}
        </button>
      </div>
      <div>
        <h4 className="mb-3 text-xs font-black uppercase tracking-widest text-muted-foreground">{t('quota.headingResources')}</h4>
        <div className="grid gap-4 sm:grid-cols-2">
          {resourceKeys.map((rk) => {
            const b = snap.resources[rk];
            const lk = RESOURCE_LABEL_KEYS[rk];
            if (!b || !lk) return null;
            const formatValue = rk === 'noteMediaStorage' ? formatStorageBytes : undefined;
            return <Bar key={rk} label={t(lk)} used={b.used} max={b.max} formatValue={formatValue} />;
          })}
        </div>
        {snap.maxNoteBodyBytes ? (
          <p className="mt-3 text-[11px] text-muted-foreground">
            {t('quota.maxNoteBodyFootnote', { size: formatStorageBytes(snap.maxNoteBodyBytes) })}
          </p>
        ) : null}
      </div>
      <div>
        <h4 className="mb-3 text-xs font-black uppercase tracking-widest text-muted-foreground">{t('quota.headingAi')}</h4>
        <div className="grid gap-4 sm:grid-cols-2">
          {(Object.keys(AI_LABEL_KEYS) as AiQuotaKey[]).map((ak) => {
            const b = ai[ak];
            if (!b) return null;
            return <Bar key={ak} label={t(AI_LABEL_KEYS[ak])} used={b.used} max={b.max} />;
          })}
        </div>
      </div>
    </div>
  );
}
